/**
 * Trainova AI — Competency Readiness Decay Calculation Engine
 * Implements PRD v1.1 (§12.1) and ADR Architecture Decision Record
 * Model A: Continuous Daily Linear Decay (10% decay per 90 days elapsed)
 */

export interface ReadinessCalculationInput {
  baselineScore: number; // e.g. 85.0 (percentage)
  assessedAt: string | Date | number;
  currentDate?: Date;
  decayRatePer90Days?: number; // default 0.10 (10%)
}

export type ReadinessStatus = 'CERTIFIED' | 'STABLE' | 'REFRESH_SOON' | 'EXPIRED_CRITICAL';

export interface ReadinessCalculationResult {
  baselineScore: number;
  currentScore: number;
  daysElapsed: number;
  decayPercentage: number;
  decayPoints: number;
  status: ReadinessStatus;
  statusLabel: string;
  statusColor: string;
  daysRemainingInCycle: number;
  isPracticeProtected: boolean;
}

export function calculateReadinessDecay({
  baselineScore,
  assessedAt,
  currentDate = new Date(),
  decayRatePer90Days = 0.10,
}: ReadinessCalculationInput): ReadinessCalculationResult {
  const assessmentTime = new Date(assessedAt).getTime();
  const now = currentDate.getTime();
  const diffMs = Math.max(0, now - assessmentTime);
  const daysElapsed = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Continuous Daily Linear: Decay = (daysElapsed / 90) * decayRatePer90Days
  const rawDecayFactor = (daysElapsed / 90) * decayRatePer90Days;
  const clampedDecayFactor = Math.min(rawDecayFactor, 0.50); // Cap max decay at 50%

  const decayPercentage = Number((clampedDecayFactor * 100).toFixed(2));
  const currentScore = Number((baselineScore * (1 - clampedDecayFactor)).toFixed(1));
  const decayPoints = Number((baselineScore - currentScore).toFixed(1));

  // 90-day cycle countdown
  const cycleDays = 90;
  const daysIntoCycle = daysElapsed % cycleDays;
  const daysRemainingInCycle = cycleDays - daysIntoCycle;

  let status: ReadinessStatus = 'CERTIFIED';
  let statusLabel = 'Certified & Active';
  let statusColor = '#10B981';

  if (daysElapsed < 30) {
    status = 'CERTIFIED';
    statusLabel = 'Certified & Active';
    statusColor = '#10B981';
  } else if (daysElapsed < 60) {
    status = 'STABLE';
    statusLabel = 'Proficiency Stable';
    statusColor = '#38BDF8';
  } else if (daysElapsed < 90) {
    status = 'REFRESH_SOON';
    statusLabel = 'Recertification Recommended';
    statusColor = '#F59E0B';
  } else {
    status = 'EXPIRED_CRITICAL';
    statusLabel = 'Decay Notice — Assessment Due';
    statusColor = '#EF4444';
  }

  return {
    baselineScore,
    currentScore,
    daysElapsed,
    decayPercentage,
    decayPoints,
    status,
    statusLabel,
    statusColor,
    daysRemainingInCycle,
    isPracticeProtected: true,
  };
}
